/**
 * Quick Reference Guide (40 CFR 262.262) – shared types.
 *
 * A large quantity generator that first becomes subject to 40 CFR 262.262 (after May 30, 2017) or
 * that is otherwise amending its contingency plan must submit a "quick reference guide" of the
 * contingency plan to local emergency responders / the LEPC, covering:
 *   (1) types/names of hazardous wastes in layman's terms + associated hazard
 *   (2) estimated maximum amount of each present at any one time
 *   (3) hazardous wastes requiring unique/special medical treatment
 *   (4) a facility map showing where waste is generated/accumulated/treated + access routes
 *   (5) a street map of the facility vs. surrounding businesses/schools/residences
 *   (6) locations of water supply (e.g. fire hydrant + flow rate)
 *   (7) on-site notification systems (e.g. fire alarm that rings off-site, smoke alarms)
 *   (8) emergency coordinator name(s) + 24/7 emergency phone number(s)
 *
 * Elements (4), (6), and (7) are covered by the existing facility Storage Location Site Map
 * (StorageSiteMapEditor.vue) - (6)/(7) via the 'water-supply'/'notification-system' feature types
 * added there. This module covers the remaining narrative elements (1,2,3,8) plus a separate
 * "street map" view (5) and the PDF export configuration.
 */

import type {
  QuickReferenceGuideEmergencyCoordinator,
  QuickReferenceGuideWasteEntry,
} from '../../tri-shared/types/hazwaste-quick-reference-guide.ts';

export type QuickReferenceGuideOrientation = 'portrait' | 'landscape';

export interface QuickReferenceGuideBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** Print-layout options captured from the Export Setup dialog, mirroring the Tier II Site Map
 * export dialog's `SiteMapExportConfig` shape (orientation/title/bounds), extended with a second
 * bounds rectangle for the street-map page. */
export interface QuickReferenceGuideExportConfig {
  orientation: QuickReferenceGuideOrientation;
  title: string;
  /** Exact geographic rectangle framed by the facility map preview (WYSIWYG), like the Tier II
   * export dialog's `bounds`. Null if the preview map had not been laid out yet, in which case the
   * PDF falls back to bounds derived from the map's center/zoom. */
  facilityMapBounds: QuickReferenceGuideBounds | null;
  /** Exact geographic rectangle framed by the street map preview. Null as for `facilityMapBounds`. */
  streetMapBounds: QuickReferenceGuideBounds | null;
  /** Position/size/font-size of the Facility Map page's header/map/legend boxes. */
  facilityMapLayout: QuickReferenceGuideFacilityMapLayout;
  /** Position/size/font-size of the Street Map page's header/map boxes (no legend). */
  streetMapLayout: QuickReferenceGuideStreetMapLayout;
  /** Editable subtitle text under each map page's title (defaults to a fixed description if blank). */
  facilityMapSubtitle: string;
  streetMapSubtitle: string;
}

export const QUICK_REFERENCE_GUIDE_DEFAULT_TITLE = 'Quick Reference Guide \u2014 Contingency Plan (40 CFR 262.262)';
export const QUICK_REFERENCE_GUIDE_DEFAULT_FACILITY_MAP_SUBTITLE =
  'Waste generation/accumulation/treatment areas, access routes, water supply, and notification systems';
export const QUICK_REFERENCE_GUIDE_DEFAULT_STREET_MAP_SUBTITLE =
  'Facility location relative to surrounding streets, businesses, schools, and residential areas';

export const QUICK_REFERENCE_GUIDE_UNIT_LABELS: Record<QuickReferenceGuideWasteEntry['maxAmountUnit'], string> = {
  lbs: 'lbs',
  kg: 'kg',
  gallons: 'gal',
};

export function deriveQuickReferenceGuideHazardDescription(
  wasteCodes: readonly string[],
  isAcuteHazardous: boolean,
): string {
  const codes = wasteCodes.map((code) => code.trim().toUpperCase());
  const labels: string[] = [];
  if (isAcuteHazardous || codes.some((code) => code.startsWith('P'))) {
    labels.push('Acutely toxic - lethal even in very small amounts');
  } else if (
    codes.some((code) => /^D0(0[4-9]|[1-3]\d|4[0-3])$/.test(code)) ||
    codes.some((code) => code.startsWith('U'))
  ) {
    labels.push('Toxic - harmful if ingested, inhaled, or absorbed');
  }
  if (codes.includes('D001')) labels.push('Ignitable - flammable or combustible');
  if (codes.includes('D002')) labels.push('Corrosive - causes skin, eye, and tissue burns');
  if (codes.includes('D003')) labels.push('Reactive - may explode or release toxic gases');
  if (!labels.length && codes.some((code) => code.startsWith('F') || code.startsWith('K'))) {
    labels.push('Listed hazardous waste');
  }
  return labels.join('; ');
}

export interface QuickReferenceGuideReadinessInput {
  wasteEntries: readonly QuickReferenceGuideWasteEntry[];
  emergencyCoordinators: readonly QuickReferenceGuideEmergencyCoordinator[];
  facilityMapFeatureTypes: readonly string[];
}

/** Returns user-facing reasons that a guide is not ready to distribute to emergency responders. */
export function quickReferenceGuideReadinessIssues(input: QuickReferenceGuideReadinessInput): string[] {
  const issues: string[] = [];
  if (!input.wasteEntries.length) issues.push('Add at least one active hazardous waste stream.');
  for (const entry of input.wasteEntries) {
    const label = entry.laymanName.trim() || 'A waste stream';
    if (!entry.laymanName.trim()) issues.push('Every waste stream needs a layman’s-terms name.');
    if (!entry.hazardDescription.trim()) issues.push(`${label} needs an associated hazard description.`);
    if (entry.maxAmount == null || !Number.isFinite(entry.maxAmount) || entry.maxAmount <= 0) {
      issues.push(`${label} needs a positive estimated maximum amount.`);
    }
  }

  if (!input.emergencyCoordinators.length) {
    issues.push('Add at least one emergency coordinator and 24/7 telephone number.');
  } else if (input.emergencyCoordinators.some((coordinator) => !coordinator.name.trim() || !coordinator.phone.trim())) {
    issues.push('Every emergency coordinator needs both a name and telephone number.');
  }

  const types = new Set(input.facilityMapFeatureTypes);
  const storageTypes = [
    'central-accumulation-area',
    'satellite-accumulation-area',
    'permitted-storage-area',
    'other-storage-area',
  ];
  if (!storageTypes.some((type) => types.has(type))) {
    issues.push(
      'Place at least one hazardous-waste generation, accumulation, or treatment area on the Facility Site Map.',
    );
  }
  if (!types.has('pathway') && !types.has('evacuation-route')) {
    issues.push('Add an access or evacuation route to the Facility Site Map.');
  }
  if (!types.has('water-supply')) issues.push('Add a water-supply location to the Facility Site Map.');
  if (!types.has('notification-system')) issues.push('Add an on-site notification system to the Facility Site Map.');

  return [...new Set(issues)];
}

/**
 * Page + map geometry (PDF points) for a given orientation - single source of truth shared by the
 * PDF renderer (quick-reference-guide-pdf.ts) and the Export Setup dialog's live preview, so the
 * on-screen map preview has EXACTLY the same aspect ratio as the printed map box. Without this, a
 * preview map sized to whatever the dialog's flex layout happens to give it (a different aspect
 * ratio than the printed rectangle) produces a stretched/distorted image once the captured bounds
 * are resized to fill the PDF's map box (`fit: 'fill'`). Both the Facility Map and Street Map
 * pages use the same full-bleed map-box formula, so one geometry function covers both.
 */
export function quickReferenceGuidePageGeometry(orientation: QuickReferenceGuideOrientation, hasLegend: boolean) {
  const portrait = orientation !== 'landscape';
  const PAGE_W = portrait ? 612 : 792; // 8.5" x 11" / 11" x 8.5"
  const PAGE_H = portrait ? 792 : 612;
  const MARGIN = 36;
  /** Extra space reserved above the map box (beyond the outer margin) for the page title/subtitle. */
  const HEADER_SPACE = 40;
  /** Extra space reserved below the map box (beyond the outer margin) for the legend - the Street
   * Map page has no legend, so it gets the full page height back for the map instead. */
  const LEGEND_SPACE = hasLegend ? 60 : 0;
  const MAP_X = MARGIN;
  const MAP_Y = MARGIN + LEGEND_SPACE;
  const MAP_W = PAGE_W - MARGIN * 2;
  const MAP_H = PAGE_H - MARGIN - HEADER_SPACE - MAP_Y;
  return { PAGE_W, PAGE_H, MARGIN, HEADER_SPACE, LEGEND_SPACE, MAP_X, MAP_Y, MAP_W, MAP_H };
}

/**
 * A draggable/resizable layout box, expressed as a fraction (0-1) of the page width/height,
 * top-left origin (y increases downward - plain CSS convention). Resolution-/orientation-
 * independent: the same fractions are re-applied against whichever PAGE_W/PAGE_H is current.
 * Mirrors tierii-shared/types/site-map.ts's `SiteMapElementRect`.
 */
export interface QuickReferenceGuideElementRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * User-adjustable position/size/font-size for the Street Map page's header/map blocks (Export
 * Setup "resize/move" controls), mirroring tierii-shared's `SiteMapLayoutOverrides`. Seeded from
 * `defaultQuickReferenceGuideStreetMapLayout()`.
 */
export interface QuickReferenceGuideStreetMapLayout {
  header: QuickReferenceGuideElementRect;
  map: QuickReferenceGuideElementRect;
  titleFontSize: number;
  subtitleFontSize: number;
}

/** As `QuickReferenceGuideStreetMapLayout`, plus the Facility Map page's legend block. Seeded from
 * `defaultQuickReferenceGuideFacilityMapLayout()`. */
export interface QuickReferenceGuideFacilityMapLayout extends QuickReferenceGuideStreetMapLayout {
  legend: QuickReferenceGuideElementRect;
  legendFontSize: number;
  /** Extra vertical gap (pt) added between legend rows, on top of the font-driven default. */
  legendRowSpacing: number;
}

/** Scale a fractional layout rect up to absolute points (still top-left origin). */
export function quickReferenceGuideLayoutRectToPoints(
  rect: QuickReferenceGuideElementRect,
  pageW: number,
  pageH: number,
): { x: number; y: number; w: number; h: number } {
  return { x: rect.x * pageW, y: rect.y * pageH, w: rect.w * pageW, h: rect.h * pageH };
}

function geometryToLayoutRect(
  g: ReturnType<typeof quickReferenceGuidePageGeometry>,
  x: number,
  yTop: number,
  w: number,
  h: number,
): QuickReferenceGuideElementRect {
  return { x: x / g.PAGE_W, y: yTop / g.PAGE_H, w: w / g.PAGE_W, h: h / g.PAGE_H };
}

/**
 * Default (fraction-based) layout boxes + font sizes for each map page, for a given orientation.
 * Seeds the Export Setup dialog's editable/draggable layout. Derived from
 * `quickReferenceGuidePageGeometry` so the un-customized defaults reproduce the original fixed
 * layout exactly.
 */
export function defaultQuickReferenceGuideStreetMapLayout(
  orientation: QuickReferenceGuideOrientation,
): QuickReferenceGuideStreetMapLayout {
  const g = quickReferenceGuidePageGeometry(orientation, false);
  return {
    header: geometryToLayoutRect(g, g.MARGIN, g.MARGIN, g.PAGE_W - g.MARGIN * 2, g.HEADER_SPACE),
    map: geometryToLayoutRect(g, g.MAP_X, g.PAGE_H - g.MAP_Y - g.MAP_H, g.MAP_W, g.MAP_H),
    titleFontSize: 14,
    subtitleFontSize: 8.5,
  };
}

export function defaultQuickReferenceGuideFacilityMapLayout(
  orientation: QuickReferenceGuideOrientation,
): QuickReferenceGuideFacilityMapLayout {
  const g = quickReferenceGuidePageGeometry(orientation, true);
  return {
    header: geometryToLayoutRect(g, g.MARGIN, g.MARGIN, g.PAGE_W - g.MARGIN * 2, g.HEADER_SPACE),
    map: geometryToLayoutRect(g, g.MAP_X, g.PAGE_H - g.MAP_Y - g.MAP_H, g.MAP_W, g.MAP_H),
    legend: geometryToLayoutRect(
      g,
      g.MARGIN,
      g.PAGE_H - g.MARGIN - g.LEGEND_SPACE + 6,
      g.PAGE_W - g.MARGIN * 2,
      g.LEGEND_SPACE - 6,
    ),
    titleFontSize: 14,
    subtitleFontSize: 8.5,
    legendFontSize: 8,
    legendRowSpacing: 0,
  };
}
